import { DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { Heading } from '@orbita-ui/core';
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Table } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import useCreateGroup from 'hooks/data/useCreateGroup';
import useGetAllGroups from 'hooks/data/useGetAllGroups';
import useGetAllRoles from 'hooks/data/useGetAllRoles';
import useDeleteGroup from 'hooks/delete/useDeleteGroup';
import useAuthService from 'hooks/useAuthService';
import useToastContext from 'hooks/useToastContext';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { camelCaseObjectToSnakeCase } from 'utils/camelCaseToSnakeCase';
import { HttpStatusCode, RealmRoles } from '../../../constants';
import * as S from './groups.styles';

type NewGroupFormInputs = {
  name: string;
  roles: string[];
};

interface GroupCreateFormProps {
  open: boolean;
  onCancel: () => void;
}

const GroupCreateForm: React.FC<GroupCreateFormProps> = ({ open, onCancel }) => {
  const { roles } = useGetAllRoles();
  const [form] = Form.useForm();
  const toast = useToastContext();
  const { isLoading: createGroupLoading, createGroup } = useCreateGroup();

  const onSubmit = async (data) => {
    try {
      const res = await createGroup(
        camelCaseObjectToSnakeCase({
          ...data,
          roles: roles.filter((role) => data.roles.includes(role.id)).map((role) => ({ id: role.id, name: role.name })),
        })
      );
      if (res.status === HttpStatusCode.Created) {
        toast.success('Exito', 'Se ha creado el grupo con exito!');
        form.resetFields();
        onCancel();
      }
    } catch (e) {
      console.error(e);
      toast.error('Error', 'No se ha podido crear el grupo, intentar nuevamente.');
    }
  };

  return (
    <Modal
      open={open}
      title="Crear nuevo grupo"
      okText="Crear"
      okButtonProps={{ loading: createGroupLoading }}
      cancelText="Cancelar"
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then(async (values) => {
            await onSubmit(values);
          })
          .catch((info) => {
            console.error('Validate Failed:', info);
          });
      }}
    >
      <Form form={form} layout="vertical" name="form_in_modal">
        <Form.Item<NewGroupFormInputs>
          name="name"
          label="Nombre de grupo"
          rules={[{ required: true, message: 'Por favor ingresar un nombre de grupo.' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item<NewGroupFormInputs>
          name="roles"
          label="Roles"
          rules={[
            { required: true, message: 'Por favor seleccionar roles.' },
            { type: 'array', min: 1, message: 'Seleccionar uno o mas roles.' },
          ]}
        >
          <Select mode="multiple">
            {roles?.map((role) => (
              <Select.Option key={role.id} value={role.id}>
                {/* <Space direction="vertical"> */}
                <Paragraph strong style={{ margin: 0 }}>
                  {role.name}
                </Paragraph>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  {role.description}
                </Paragraph>
                {/* </Space> */}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const SettingsGroups = () => {
  const toast = useToastContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { issuer } = useParams<{ issuer: string }>();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const { remove, isLoading: removeGroupLoading} = useDeleteGroup();

  const { groups, pageInfo, isLoading, execute: retryGetGroups } = useGetAllGroups(paginationModel.page, paginationModel.pageSize);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const { userHasRole } = useAuthService();
  const handleCreateUserClick = () => {
    setShowNewGroupModal(true);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  const confirm = async (id: string) => {
    try {
      const res = await remove(id);
      if (res.status === HttpStatusCode.NoContent) {
        toast.success('Grupo eliminado!', 'El grupo se elimino exitosamente.');
        retryGetGroups()
      } else {
        throw new Error('Failed to delete');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error!', 'El grupo no se ha eliminado. Intentar nuevamente.');
    }
  };

  return (
    <>
      <Heading bold size="xl">
        Mis Grupos
      </Heading>
      <S.GroupsActionsContainer>
        {userHasRole([RealmRoles.GroupsAllWrite]) && (
          <Button icon={<PlusOutlined />} type="primary" onClick={handleCreateUserClick}>
            Crear grupo
          </Button>
        )}
      </S.GroupsActionsContainer>
      <Table
        scroll={{ x: true }}
        loading={isLoading}
        columns={[
          {
            dataIndex: 'id',
            title: 'ID',
            key: 'id',
          },
          {
            dataIndex: 'name',
            title: 'Nombre',
            key: 'name',
          },
          {
            dataIndex: 'actions',
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => {
              return (
                <Space>
                  <Button
                    size="small"
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`${pathname}/groups/${record.id}`)}
                  >
                    Ver
                  </Button>
                  <Popconfirm
                    title="Eliminar grupo"
                    description={`Estas seguro que queres eliminar el grupo: ${record.name}?`}
                    onConfirm={() => {
                      confirm(record.id);
                    }}
                    okText="Si"
                    cancelText="No"
                    okButtonProps={{ loading: removeGroupLoading }}
                  >
                    {record.name !== `${issuer}_admins` && (
                      <Button size="small" type="link" icon={<DeleteOutlined />} danger>
                        Eliminar
                      </Button>
                    )}
                  </Popconfirm>
                </Space>
              );
            },
          },
        ]}
        dataSource={groups}
        pagination={{
          total: pageInfo.total,
          defaultPageSize: 10,
          defaultCurrent: 1,
          onChange: handlePaginationChange,
          pageSizeOptions: ['10', '25', '50', '100'],
          showSizeChanger: true,
        }}
      />

      <GroupCreateForm open={showNewGroupModal} onCancel={() => setShowNewGroupModal(false)} onCreate={console.log} />
    </>
  );
};

export default SettingsGroups;
